import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Form, Row, Col } from 'react-bootstrap';
import { FiCloudOff, FiRefreshCw, FiEye } from 'react-icons/fi';
import { getActivityForecastsNotTriggered } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsForecasts.css';

const EMPTY_ACTIVITY_FORECASTS_NOT_TRIGGERED_FILTERS = {
    company_name: '',
    policy_code: '',
    reservation_number: '',
    client_email: '',
    created_date: '',
};

const AdminActivityForecastsNotTriggered = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [forecasts, setForecasts] = useState([]);
    const [meta, setMeta] = useState({
        total: 0,
        skip: 0,
        limit: 10,
        page: 1,
        total_pages: 0,
    });

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        clearSearchState,
    } = usePersistedSearchState('activity-forecasts-not-triggered', EMPTY_ACTIVITY_FORECASTS_NOT_TRIGGERED_FILTERS);

    const handleDetails = (item) => {
        const forecastNotTriggeredId = getDeep(item, ['forecast_not_triggered_id']);
        if (forecastNotTriggeredId) {
            navigate(`/activity/forecasts-not-triggered/${forecastNotTriggeredId}`);
        }
    };

    // Charger les prévisions non déclenchées avec pagination et filtres
    const loadForecasts = useCallback(async (page = 1, filtersToUse = null) => {
        try {
            setLoading(true);
            setError(null);
            const skip = (page - 1) * itemsPerPage;
            const params = {
                skip,
                limit: itemsPerPage,
            };

            // Utiliser les filtres fournis ou les filtres appliqués
            const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

            // Ajouter les filtres non vides
            Object.keys(activeFilters).forEach((key) => {
                if (activeFilters[key] && activeFilters[key].trim() !== '') {
                    params[key] = activeFilters[key].trim();
                }
            });

            const data = await getActivityForecastsNotTriggered(params);
            setForecasts(data?.data || []);
            setMeta(data?.meta || {
                total: 0,
                skip,
                limit: itemsPerPage,
                page,
                total_pages: 0,
            });
        } catch (err) {
            const errorMsg = handleApiError(err, 'Erreur lors du chargement des prévisions non déclenchées');
            setError(errorMsg);
            setForecasts([]);
        } finally {
            setLoading(false);
        }
    }, [itemsPerPage, appliedFilters]);

    useEffect(() => {
        loadForecasts(currentPage);
    }, [currentPage, loadForecasts]);

    // Pagination côté serveur - utiliser les métadonnées de l'API
    const totalPages = meta.total_pages || Math.max(1, Math.ceil(meta.total / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + forecasts.length, totalItems);

    const handleRefresh = () => {
        loadForecasts(currentPage);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters({ ...filters });
        setCurrentPage(1);
        loadForecasts(1, filters);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_ACTIVITY_FORECASTS_NOT_TRIGGERED_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadForecasts(1, emptyFilters);
        }, 100);
    };

    // Calculate pagination range (show max 5 pages around current)
    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    // Utiliser directement les prévisions de l'API (pas de filtrage côté client)
    const currentForecasts = forecasts;

    const getDeep = (obj, path, fallback = 'N/A') => {
        try {
            const val = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
            return val ?? fallback;
        } catch {
            return fallback;
        }
    };

    return (
        <div className="dashboard-page admin-trips-forecasts-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiCloudOff className="me-2" />
                                Activity - Gestion Prévisions Non Déclenchées
                            </h2>
                            <p className="text-muted mb-0">Prévisions non déclenchées liées aux activités</p>
                        </div>
                    </div>

                    {error && (
                        <Alert variant="danger" className="mt-3">
                            {error}
                        </Alert>
                    )}

                    {/* Filtres avancés */}
                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Partenaire (company name)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="company_name"
                                            value={filters.company_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: green river"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code police</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="policy_code"
                                            value={filters.policy_code}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: 280007906"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reservation_number"
                                            value={filters.reservation_number}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Numéro de réservation"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email client</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="client_email"
                                            value={filters.client_email}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Email du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date de création</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="created_date"
                                            value={filters.created_date}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <Button
                            variant="outline-secondary"
                            onClick={handleRefresh}
                            disabled={loading}
                            className="d-flex align-items-center"
                        >
                            <FiRefreshCw className={`me-2 ${loading ? 'spinning' : ''}`} />
                            Actualiser
                        </Button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement des prévisions non déclenchées...</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-5">
                            <Alert variant="danger">{error}</Alert>
                        </div>
                    ) : currentForecasts.length === 0 ? (
                        <div className="text-center py-5">
                            <FiCloudOff size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucune prévision non déclenchée disponible</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <div className="table-scroll">
                                <Table hover striped className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Code</th>
                                            <th>Code police</th>
                                            <th>Numéro réservation</th>
                                            <th>Client</th>
                                            <th>Date création</th>
                                            <th>Statut</th>
                                            <th>Basé sur</th>
                                            <th className="text-end col-actions">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(currentForecasts) && currentForecasts.length > 0 ? (
                                            currentForecasts.map((item, idx) => (
                                                <tr key={getDeep(item, ['forecast_not_triggered_id'], idx)}>
                                                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                        {getDeep(item, ['code'])}
                                                    </td>
                                                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                        {getDeep(item, ['policy_info', 'policy_code'])}
                                                    </td>
                                                    <td>{getDeep(item, ['activity_info', 'reservation_number'])}</td>
                                                    <td>
                                                        {getDeep(item, ['client_info', 'first_name'])} {getDeep(item, ['client_info', 'last_name'])}
                                                    </td>
                                                    <td>{getDeep(item, ['created_date'])}</td>
                                                    <td>
                                                        <span className={`badge bg-${getDeep(item, ['status']) === 'Created' ? 'info' : 'secondary'}`}>
                                                            {getDeep(item, ['status'])}
                                                        </span>
                                                    </td>
                                                    <td>{getDeep(item, ['trigger_based_on'])}</td>
                                                    <td className="text-end col-actions">
                                                        <Button variant="outline-info" size="sm" className="me-2" onClick={() => handleDetails(item)} title="Détails" aria-label="Détails">
                                                            <FiEye />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center">
                                                    Aucune prévision non déclenchée trouvée
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalItems > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <div className="text-muted">
                                Affichage de {indexOfFirstItem + 1} à {indexOfLastItem} sur {totalItems} prévision{totalItems > 1 ? 's' : ''} non déclenchée{totalItems > 1 ? 's' : ''}
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.First
                                    onClick={() => {
                                        setCurrentPage(1);
                                        loadForecasts(1);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => {
                                        const prevPage = currentPage - 1;
                                        setCurrentPage(prevPage);
                                        loadForecasts(prevPage);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                {(() => {
                                    const { startPage, endPage } = getPaginationRange();
                                    const pages = [];

                                    // Show first page if not in range
                                    if (startPage > 1) {
                                        pages.push(
                                            <Pagination.Item key={1} onClick={() => {
                                                setCurrentPage(1);
                                                loadForecasts(1);
                                            }}>
                                                1
                                            </Pagination.Item>
                                        );
                                        if (startPage > 2) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-start" />);
                                        }
                                    }

                                    // Show pages in range
                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <Pagination.Item
                                                key={i}
                                                active={i === currentPage}
                                                onClick={() => {
                                                    setCurrentPage(i);
                                                    loadForecasts(i);
                                                }}
                                            >
                                                {i}
                                            </Pagination.Item>
                                        );
                                    }

                                    // Show last page if not in range
                                    if (endPage < totalPages) {
                                        if (endPage < totalPages - 1) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-end" />);
                                        }
                                        pages.push(
                                            <Pagination.Item key={totalPages} onClick={() => {
                                                setCurrentPage(totalPages);
                                                loadForecasts(totalPages);
                                            }}>
                                                {totalPages}
                                            </Pagination.Item>
                                        );
                                    }

                                    return pages;
                                })()}
                                <Pagination.Next
                                    onClick={() => {
                                        const nextPage = currentPage + 1;
                                        setCurrentPage(nextPage);
                                        loadForecasts(nextPage);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => {
                                        setCurrentPage(totalPages);
                                        loadForecasts(totalPages);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminActivityForecastsNotTriggered;
